// 1a · Connexion (accès sur invitation) et 1b · changement forcé du mot de passe provisoire.
import { PASSWORD_RULES, passwordScore } from '@crenow/shared';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../api.ts';
import { ephemeral } from '../ephemeral.ts';
import { useSession } from '../session.tsx';
import { Field, FormError, Logo } from '../ui.tsx';


export function LoginPage() {
  const { login } = useSession();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      ephemeral.provisional = password;
      await login(identifier, password);
    } catch (caught) {
      ephemeral.provisional = null;
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  return (
    <main className="screen login">
      <div className="login-main">
        <div className="login-brand">
          <Logo />
          <h1 className="h1-login">CréNow!</h1>
          <p className="login-lead">Le bon moment pour aller aux champignons, à la pêche ou semer.</p>
        </div>
        <form className="login-form" onSubmit={submit}>
          <Field label="Identifiant" name="username" autoComplete="username" autoCapitalize="none" spellCheck={false} required value={identifier} onChange={(event) => setIdentifier(event.target.value)} />
          <Field label="Mot de passe" name="password" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} />
          <FormError error={error} />
          <button className="btn btn-primary login-submit" disabled={busy}>
            Se connecter
          </button>
        </form>
      </div>
      <p className="login-foot">Pas de compte ? L’accès se fait sur invitation. Mot de passe perdu : contactez votre administrateur.</p>
    </main>
  );
}

const GAUGE_COLORS = ['var(--danger)', 'var(--s-imm)', 'var(--s-imm)', 'var(--s-run)'];

export function ChangePasswordPage() {
  const { refresh, logout } = useSession();
  const [current, setCurrent] = useState(ephemeral.provisional ?? '');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const rules = [
    ...PASSWORD_RULES.map((rule) => ({ label: rule.label, ok: rule.test(next) })),
    { label: 'Confirmation identique', ok: next.length > 0 && next === confirm },
  ];
  const score = passwordScore(next);
  const ready = rules.every((rule) => rule.ok) && current.length > 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await api('PUT', '/account/password', { currentPassword: current, newPassword: next });
      ephemeral.provisional = null;
      await refresh();
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  return (
    <form className="screen" onSubmit={submit}>
      <div className="password-main">
        <div className="password-head">
          <span className="tag imm">PREMIÈRE CONNEXION</span>
          <h1 className="h1-password">Choisissez votre mot de passe</h1>
          <p className="password-lead">Le mot de passe temporaire fourni par l’administrateur doit être remplacé avant d’accéder à l’application.</p>
        </div>
        <div className="password-fields">
          {!ephemeral.provisional && (
            <Field label="Mot de passe temporaire" type="password" autoComplete="current-password" required value={current} onChange={(event) => setCurrent(event.target.value)} />
          )}
          <Field label="Nouveau mot de passe" type="password" autoComplete="new-password" placeholder="12 caractères minimum" value={next} onChange={(event) => setNext(event.target.value)} />
          <div className="gauge" role="meter" aria-label="Solidité du mot de passe" aria-valuemin={0} aria-valuemax={4} aria-valuenow={score}>
            {[0, 1, 2, 3].map((index) => (
              <span key={index} style={{ background: index < score ? GAUGE_COLORS[Math.max(0, score - 1)] : 'var(--surface2)' }} />
            ))}
          </div>
          <Field label="Confirmer" type="password" autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} />
          <ul className="rules">
            {rules.map((rule) => (
              <li key={rule.label} className={rule.ok ? 'ok' : undefined}>
                <span aria-hidden="true">{rule.ok ? '✓' : ''}</span>
                {rule.label}
                <span className="sr-only">{rule.ok ? ' : rempli' : ' : à remplir'}</span>
              </li>
            ))}
          </ul>
          <FormError error={error} />
        </div>
      </div>
      <div className="password-foot">
        <button className="btn btn-primary btn-block" disabled={!ready || busy}>
          Enregistrer et continuer
        </button>
        <button type="button" className="link-btn" onClick={() => void logout()}>
          Se déconnecter
        </button>
      </div>
    </form>
  );
}
